import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, MenuItem } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { useContext } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputDate } from "../../../components/form/FormInputDate";
import { FormInputText } from "../../../components/form/FormInputText";
import MyModal from "../../../components/MyModal";
import ActionButtons from "../../../components/MyModal/ActionButtons";

import { CANCELLED, COMPLETED } from "../../../constants";
import formatStatus from "../../../utils/formatStatusName";

const formatDateInput = (date?: string) =>
  date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";

type Props = {
  onClose: () => void;
  openSucessAlert: () => void;
  openErrorAlert: () => void;
  refreshPage: () => void;
  setActionType?: (type: string) => void;
  revalidationToEdit: {
    id: number;
    vin: string;
    serviceLost: string;
    odometer: any;
    osNumber: number;
    revisionDate: string;
    status: string;
    dn: string;
  };
};

export interface FormValues {
  vin: string;
  serviceLost: string;
  odometer: number;
  osNumber: number;
  revisionDate: string;
  status: string;
  dn: string;
}

export default function ModalEditRevalidation({
  onClose,
  openSucessAlert,
  openErrorAlert,
  refreshPage,
  revalidationToEdit,
  setActionType,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const status = [COMPLETED, CANCELLED];

  const formData = yup.object().shape({
    vin: yup.string().label(t("revalidation.edit.chassi")),
    odometer: yup.string().required(t("revalidation.edit.odometerError")),
    osNumber: yup.string().required(t("revalidation.edit.osNumberError")),
    revisionDate: yup.string().required(t("revalidation.edit.revisionDate")),
    serviceLost: yup.string().label(t("revalidation.edit.lostService")),
    status: yup.string().label(t("revalidation.edit.status")),
    dn: yup.string().required(t("revalidation.edit.dnCode")),
  });

  const { register, control, handleSubmit, formState } = useForm<FormValues>({
    resolver: yupResolver(formData) as any,
    defaultValues: {
      vin: revalidationToEdit?.vin ?? "",
      odometer: revalidationToEdit?.odometer,
      osNumber: revalidationToEdit?.osNumber,
      revisionDate: formatDateInput(revalidationToEdit?.revisionDate),
      serviceLost: revalidationToEdit?.serviceLost ?? "",
      status: revalidationToEdit?.status ?? "",
      dn: revalidationToEdit?.dn ?? "",
    },
  });

  const { errors } = formState;

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const editRevalidationService: SubmitHandler<FormValues> = async (values) => {
    try {
      await axios.put(
        `${CONFIG.SOB_API}revalidate-plan`,
        {
          id: revalidationToEdit.id,
          odometer: values.odometer,
          osNumber: values.osNumber,
          revisionDate: values.revisionDate,
          status: values.status,
          dn: values.dn,
        },
        tokenHeaders,
      );
      onClose();
      refreshPage();
      openSucessAlert();
      setActionType?.("edit");
    } catch (error: any) {
      onClose();
      openErrorAlert();
    }
  };

  return (
    <MyModal
      title={t("revalidation.edit.titleModal")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(editRevalidationService)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("revalidation.edit.vehicle")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="vin"
            label={t("revalidation.edit.chassi")}
            {...register("vin")}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInputText
            id="editOdometer"
            type="number"
            label={t("revalidation.edit.odometer")}
            inputProps={{ max: 999999 }}
            error={errors.odometer}
            {...register("odometer")}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("revalidation.edit.service")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="osNumber"
            type="number"
            label={t("revalidation.edit.osNumber")}
            inputProps={{ max: 9999999999 }}
            error={errors.osNumber}
            {...register("osNumber")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInputDate
            name="revisionDate"
            control={control}
            label={t("revalidation.edit.revisionDate")}
            minDate={dayjs("1900-01-01")}
            maxDate={dayjs("2100-01-01")}
            error={errors.revisionDate}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }} />
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="serviceLost"
            label={t("revalidation.edit.lostService")}
            {...register("serviceLost")}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} style={{ height: "10px" }}>
          <FormInputText
            select
            id="status"
            label={t("revalidation.edit.status")}
            {...register("status")}
          >
            {status.map((status) => (
              <MenuItem key={status} value={status}>
                {formatStatus(status)}
              </MenuItem>
            ))}
          </FormInputText>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("revalidation.edit.dealer")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="dn"
            type="number"
            label={t("revalidation.edit.dnCode")}
            inputProps={{ max: 999 }}
            error={errors.dn}
            {...register("dn")}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("revalidation.edit.requiredFields")}
      </p>

      <ActionButtons
        typeButton="submit"
        saveName={t("button.Save")}
        enableLine={false}
      />
    </MyModal>
  );
}
